import { type FC, useState } from 'react'
import { Users, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { TargetingRuleBuilder } from './TargetingRuleBuilder'
import { UserSearchSelect } from './UserSearchSelect'
import { generateKeyFromName } from '@/lib/utils'
import type {
    CreateFeatureFlagGroupRequest,
    FeatureFlagGroup,
    FeatureFlagGroupType,
    LogicalOperator,
    TargetableField,
} from '@/types/feature-flag'

interface CreateEditGroupModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit: (payload: CreateFeatureFlagGroupRequest) => void
    group?: FeatureFlagGroup | null
    fields: TargetableField[]
    isSubmitting?: boolean
}

const buildInitialState = (group?: FeatureFlagGroup | null) => ({
    key: group?.key ?? '',
    name: group?.name ?? '',
    description: group?.description ?? '',
    type: group?.type ?? ('STATIC' as FeatureFlagGroupType),
    userIds: group?.userIds ?? [],
    rules: group?.rules ?? [],
    ruleOperator: group?.ruleOperator ?? ('AND' as LogicalOperator),
})

export const CreateEditGroupModal: FC<CreateEditGroupModalProps> = ({
    open,
    onOpenChange,
    onSubmit,
    group,
    fields,
    isSubmitting = false,
}) => {
    const isEditing = Boolean(group)
    // Remounted by the parent on every open, so this initial state is fresh.
    const [state, setState] = useState(() => buildInitialState(group))

    /** Once the key is edited by hand, the name stops driving it. */
    const [keyEditedManually, setKeyEditedManually] = useState(false)

    const set = <K extends keyof typeof state>(
        field: K,
        value: (typeof state)[K]
    ) => setState(prev => ({ ...prev, [field]: value }))

    const handleNameChange = (name: string) => {
        setState(prev => ({
            ...prev,
            name,
            key:
                isEditing || keyEditedManually
                    ? prev.key
                    : generateKeyFromName(name),
        }))
    }

    const canSubmit =
        state.key.trim().length > 1 &&
        state.name.trim().length > 1 &&
        !isSubmitting

    const handleSubmit = () => {
        if (!canSubmit) return

        const payload: CreateFeatureFlagGroupRequest = {
            key: state.key.trim(),
            name: state.name.trim(),
            description: state.description.trim() || undefined,
            type: state.type,
        }

        if (state.type === 'STATIC') {
            payload.userIds = state.userIds
        } else {
            payload.rules = state.rules
            payload.ruleOperator = state.ruleOperator
        }

        onSubmit(payload)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl sm:max-w-2xl sm:rounded-2xl"
            >
                <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white">
                    <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <Users className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    {isEditing ? 'Edit group' : 'New group'}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    A reusable audience you can point several
                                    flags at.
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <div className="space-y-5 px-6 py-5">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="group-name">Name</Label>
                                    <Input
                                        id="group-name"
                                        value={state.name}
                                        placeholder="Internal testers"
                                        onChange={event =>
                                            handleNameChange(event.target.value)
                                        }
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="group-key">Key</Label>
                                    <Input
                                        id="group-key"
                                        value={state.key}
                                        disabled={isEditing}
                                        placeholder="internal_testers"
                                        className="font-mono"
                                        onChange={event => {
                                            setKeyEditedManually(true)
                                            set(
                                                'key',
                                                generateKeyFromName(
                                                    event.target.value
                                                )
                                            )
                                        }}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {isEditing
                                            ? 'Cannot be changed after creation.'
                                            : 'Generated from the name.'}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="group-description">
                                    Description
                                </Label>
                                <Textarea
                                    id="group-description"
                                    value={state.description}
                                    onChange={event =>
                                        set('description', event.target.value)
                                    }
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select
                                    value={state.type}
                                    onValueChange={value =>
                                        set(
                                            'type',
                                            value as FeatureFlagGroupType
                                        )
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="STATIC">
                                            Static — a fixed list of users
                                        </SelectItem>
                                        <SelectItem value="DYNAMIC">
                                            Dynamic — anyone matching rules
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {state.type === 'STATIC' ? (
                                <div className="space-y-2">
                                    <Label>Members</Label>
                                    <UserSearchSelect
                                        selectedIds={state.userIds}
                                        onChange={ids => set('userIds', ids)}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label>Rules</Label>
                                    <TargetingRuleBuilder
                                        rules={state.rules}
                                        onChange={rules => set('rules', rules)}
                                        ruleOperator={state.ruleOperator}
                                        onRuleOperatorChange={operator =>
                                            set('ruleOperator', operator)
                                        }
                                        fields={fields.filter(
                                            field => field.source === 'user'
                                        )}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Groups match on user attributes only —
                                        device and version limits belong on the
                                        flag itself.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t px-6 py-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-[#17A2B8] hover:bg-[#148c9e]"
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                        >
                            {isSubmitting
                                ? 'Saving...'
                                : isEditing
                                  ? 'Save changes'
                                  : 'Create group'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
